import React, { useState } from 'react';

import { getErrorMessage } from '~libs/axiosWrapper';

import { Button, ClickableIcon, ErrorResponse, Modal } from '~components';
import { faTrash } from '~components/icon';

import { useDeleteReconciliationMutation, useGetReconciliationQuery } from '~support-at-home/actions/reconciliations';

import { Resolve } from './reconciliations-wizard';

interface ComponentProps {
  isOpen: boolean;
  setIsOpen: (toggle: boolean) => void;
  id: string;
}

// Placeholder until have more details on what editing a reconciliation involves
const EditReconModal: React.FC<ComponentProps> = ({ isOpen, setIsOpen, id }) => {
  const { data, isLoading } = useGetReconciliationQuery({ id }, { skip: !id || !isOpen });
  const [deleteReconciliation, { isLoading: isDeleting, error: deleteError }] = useDeleteReconciliationMutation();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // Check if any recon items are linked to claim items
  // Only compute when data is defined to avoid false negatives during loading
  const hasReconciledItems = data ? (data.breakdown?.length ?? 1) > 0 : true;

  const onClose = () => {
    setDeleteModalOpen(false);
    setIsOpen(false);
  };

  const onDelete = async () => {
    const result = await deleteReconciliation({ id });
    setDeleteModalOpen(false);
    if ('data' in result) {
      setIsOpen(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      size="xl"
      isOpen={isOpen}
      setIsOpen={onClose}
      header="Edit Reconciliation"
      headerClassName="header"
      className="reconciliation-wizard-modal"
      unmountOnClose
      footerClassName="justify-content-between"
      footer={
        <>
          <ClickableIcon
            id="recon-delete-icon"
            icon={faTrash}
            className="mx-2"
            color="danger"
            onClick={() => setDeleteModalOpen(true)}
            toolTip={
              hasReconciledItems ? 'Cannot delete: One or more items have been reconciled' : 'Delete reconciliation'
            }
            disabled={hasReconciledItems || isDeleting || isLoading}
            showTooltipWhenDisabled
          />
          <Button onClick={onClose}>Close</Button>
        </>
      }
    >
      <Modal
        isOpen={deleteModalOpen}
        setIsOpen={setDeleteModalOpen}
        headerClassName="bg-warning"
        header="Warning"
        footer={
          <>
            <Button color="danger" loading={isDeleting} onClick={onDelete}>
              Delete
            </Button>
            <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
          </>
        }
      >
        <div className="mb-3">Are you sure you want to delete this reconciliation?</div>
        <div className="fw-bold">This action is irreversible.</div>
      </Modal>
      {deleteError && <ErrorResponse message={getErrorMessage(deleteError)} />}
      <Resolve value={{ reconciliationId: id ?? '' }} />
    </Modal>
  );
};

export default EditReconModal;
